import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ProductNumberKind } from 'hooks/webapi/useClient';
import { useInsurancePolicy } from 'pages/InsurancePolicy/useInsurancePolicy';
import { useLocation } from 'react-router-dom';
import { useSeon } from 'hooks/useSeon';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

export type Application = {
  amount: number;
  confirmed: boolean;
  created: string;
  loanApplicationEnhancements?: string[];
  insurancePrice?: number;
  expired: boolean;
  id: number;
  productInfo: ProductNumberKind;
  pendingProcessing: boolean;
  status: 'NEW' | 'OPEN' | 'CLOSED';
  term: number;
  type:
    | 'SMS'
    | 'WEB'
    | 'MOBILE'
    | 'PHONE'
    | 'OFFLINE_TRANSFER'
    | 'OFFLINE_CASH'
    | 'BROKER'
    | 'POS'
    | 'FINTONIC'
    | 'PRESTALO';
  productNumber: string;
  resolutionDetail: string;
  resolutionMessage?: string;
  typeDetail: string;
  resolution: string;
  _embedded: {
    proposal?: {};
  };
};

type CreateApplicationOptions = {
  body: {
    affiliateInfo?: {
      provider?: string;
      partner?: string;
      token?: string;
      cookieCreated?: string;
    };
    productNumber?: string;
    amount: number;
    term: number;
    monthlyPayment?: number;
    type?:
      | 'SMS'
      | 'WEB'
      | 'MOBILE'
      | 'PHONE'
      | 'OFFLINE_TRANSFER'
      | 'OFFLINE_CASH'
      | 'BROKER'
      | 'POS'
      | 'FINTONIC'
      | 'PRESTALO';
    source?:
      | 'DESKTOP'
      | 'MOBILE'
      | 'CALL_CENTER'
      | 'BROKER'
      | 'IOS'
      | 'ANDROID'
      | 'HYBRID_IOS'
      | 'HYBRID_ANDROID'
      | 'EXTRA_SERVICE_APPLICATION'
      | 'CRM'
      | 'WEB_OTHER_PRODUCT';
    autoRepay?: boolean; // Whether the loan should be auto-repaid automatically on due date
    typeDetail?:
      | 'MOBILE_APPLICATION'
      | 'ESHOP'
      | 'E_WALLET_ANDROID'
      | 'E_WALLET_IOS'
      | 'E_WALLET_BOON'
      | 'CALL_CENTER'
      | 'CAMPAIGN_DISTRIBUTED'
      | 'LEAD_GENERATOR'
      | 'EXTRA_SERVICE_APPLICATION'
      | 'MULTIFACTOR_AUTHENTIFICATION'
      | 'IOS'
      | 'HYBRID_IOS'
      | 'HYBRID_ANDROID'
      | 'ANDROID'
      | 'WEB_OFFER'
      | 'PHONE_OFFER'
      | 'AFFILIATE'
      | 'CRM'
      | 'E_MONEY'
      | 'MOBILE_APPLICATION_OFFER'
      | 'REFINANCE'
      | 'OFFLINE_OFFER';
    discountPercent?: number;
    newSourceVersion?: boolean; // Whether the application came from a new front end, used for data customAnalytics only
    partnerRepresentativeId?: number;
    ioBlackBox?: string;
    firstPaymentDueDate?: string;
    calculatorId?: number;
    customTermOffered?: boolean;
  };
  postApplicationCreated: (
    application: CreatedApplicationResponse,
  ) => Promise<void>;
};

export type CreatedApplicationResponse = {
  rejected: boolean;
};

type ApplicationContextType = {
  application?: Application | null;
  fetchApplication: () => Promise<Application | undefined>;
  createApplication: ({
    body,
  }: CreateApplicationOptions) => Promise<CreatedApplicationResponse | void>;
  confirmApplication: ({
    postApplicationConfirmed,
  }: {
    postApplicationConfirmed: () => Promise<void>;
  }) => Promise<ConfirmApplicationResponse | void>;
  acceptLatestTerms: () => Promise<void>;
  clearApplication: () => void;
};

type ConfirmApplicationResponse = {
  status: 'IN_PROGRESS';
};

const ApplicationContext = createContext<ApplicationContextType>(
  {} as ApplicationContextType,
);

const useApplication = (): ApplicationContextType =>
  useContext(ApplicationContext);

const ApplicationProvider = ({ children }: Props): JSX.Element => {
  const [application, setApplication] = useState<
    Application | undefined | null
  >();
  const { getIsInsurancePolicyEnabled } = useInsurancePolicy();
  const { sendSeonFraudInfo } = useSeon();
  const { showError } = useError();
  const location = useLocation();

  const fetchApplication = useCallback(async (): Promise<
    Application | undefined
  > => {
    try {
      const { data: fetchedApplication } = await axiosInstance.get(
        '/client/application',
        {
          headers: {
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          },
        },
      );
      setApplication(fetchedApplication);
      return fetchedApplication;
    } catch (e) {
      setApplication(null);
      showError();
      throw e;
    }
  }, [showError]);

  useEffect(() => {
    const prefetch = async () => {
      if (application === undefined && isLogged()) {
        try {
          await fetchApplication();
        } catch {}
      }
    };
    prefetch();
  }, [location]);

  const createApplication = useCallback(
    async ({
      body,
      postApplicationCreated,
    }: CreateApplicationOptions): Promise<CreatedApplicationResponse | void> => {
      try {
        const { data: createdApplicationResponse } = await axiosInstance.post(
          '/client/application',
          body,
        );

        const { data: clientLoanCount } = await axiosInstance.get(
          '/client/loans',
        );

        if (!clientLoanCount) {
          try {
            await sendSeonFraudInfo();
          } catch {}
        }

        await postApplicationCreated(createdApplicationResponse);
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError],
  );

  const confirmApplication = useCallback(
    async ({
      postApplicationConfirmed,
    }: {
      postApplicationConfirmed: () => void;
    }): Promise<ConfirmApplicationResponse | void> => {
      try {
        const isInsurancePolicyEnabled = getIsInsurancePolicyEnabled();
        const { data } = await axiosInstance.put('/client/application', {
          insurance: isInsurancePolicyEnabled,
        });

        postApplicationConfirmed();

        return data;
      } catch (e) {
        showError();
        throw e;
      }
    },
    [showError],
  );

  const acceptLatestTerms = useCallback(async () => {
    try {
      const { data } = await axiosInstance.post(
        '/client/accept-latest-agreement-version',
      );

      return data;
    } catch (e) {
      showError();
      throw e;
    }
  }, [showError]);

  const clearApplication = () => setApplication(null);

  const applicationContextValue = useMemo(
    () => ({
      application,
      fetchApplication,
      createApplication,
      confirmApplication,
      acceptLatestTerms,
      clearApplication,
    }),
    [
      application,
      fetchApplication,
      createApplication,
      confirmApplication,
      acceptLatestTerms,
      clearApplication,
    ],
  );

  return (
    <ApplicationContext.Provider value={applicationContextValue}>
      {children}
    </ApplicationContext.Provider>
  );
};

export { useApplication as default, ApplicationProvider };
